// V62.5 — Comprehensive periodic table data module.
//
// Replaces src/data/elements.json. All 118 elements with ~22 properties
// each. Used by:
//   • src/chem/PeriodicTable.jsx — the interactive periodic table
//   • src/chem/PeriodicCompare.jsx — multi-element compare view (NEW V62.5)
//   • src/chem/EquationBalancer.jsx, MolecularWeight.jsx, MoleCalculator.jsx,
//     Stoichiometry.jsx — chem calculator tools (read symbol/mass only)
//   • src/games/periodicAdapter.js — periodic-table game adapter
//   • V64+ chem quiz distractor clustering — via getPeriodicNeighbors()
//
// Data sources (per field, in order of precedence):
//   • Atomic mass:               IUPAC 2021 conventional atomic weights;
//                                synthetic elements bracketed [longest-lived isotope mass]
//   • Electron configuration:    NIST Atomic Spectra Database; standard textbook form
//   • State at STP:              Standard textbook (Brown/LeMay, Chang)
//   • Density, melt, boil:       CRC Handbook of Chemistry 95th ed.; PubChem for missing
//   • Electronegativity:         Pauling scale (allred-rochow not used) — standard chem
//   • Electron affinity:         NIST CCCBDB; reported in kJ/mol, sign convention positive = exothermic attachment
//   • Ionization energy:         NIST Atomic Spectra; first IE in kJ/mol
//   • Atomic radius:             Empirical (Slater 1964 / Clementi); pm
//   • Common ionic charges:      Standard HS/AP chem convention (Brown/LeMay tables)
//   • Common oxidation states:   IUPAC Red Book + standard chem practice (most-stable bolded in textbooks)
//   • Discovery year + by:       Standard chemistry history references; ancient elements use "Ancient"
//                                (Cu/Au/Ag/Fe/Pb/Sn/C/S/Hg/As/Sb/Bi all "Ancient" — pre-1500)
//
// Notes on null values:
//   • Numeric fields are null (not empty string) when value unknown/unmeasured
//   • Common for superheavy elements (104+) — most properties theoretical/unmeasured
//   • Noble gases He, Ne, Ar typically lack measured EN — null (not extrapolated values)
//   • Some EA values are negative ("electron unbound") — represented as null per
//     standard HS chem convention (only positive/stable EAs are taught)
//
// Bugs fixed from V60.10 elements.json:
//   • Element 13 (Al)  — was category:"transition", should be "post-transition"
//   • Element 31 (Ga)  — was category:"transition", should be "post-transition"
//   • Element 49 (In)  — was category:"transition", should be "post-transition"
//   • Element 50 (Sn)  — was category:"transition", should be "post-transition"
//   • Element 81 (Tl)  — was category:"transition", should be "post-transition"
//   • Element 82 (Pb)  — was category:"transition", should be "post-transition"
//   • Element 83 (Bi)  — was category:"transition", should be "post-transition"
//   • Element 115 (Mc) — group field was "halogen" string; now numeric group 15
//   • Element 116 (Lv) — group field was "noble gas" string; now numeric group 16
//   • Element 117 (Ts) — group field was "alkali metal" string; now numeric group 17
//   • Element 118 (Og) — group field was "alkaline earth metal" string; now numeric group 18
//   • All empty-string numeric fields normalized to null
//
// f-block convention: Lanthanides (57-71) and actinides (89-103) have
// group: null. Some IUPAC conventions put La/Lu/Ac/Lr in group 3, but the
// spec explicitly assigns null to the entire f-block. See SPEC §"Field list".

// ─── Category labels ────────────────────────────────────────────────
// Derived from `category` field. Centralized here so render code doesn't
// duplicate the mapping.
export const CATEGORY_LABELS = {
  alkali:            "Alkali metal",
  alkaline:          "Alkaline earth",
  transition:        "Transition metal",
  "post-transition": "Post-transition metal",
  metalloid:         "Metalloid",
  nonmetal:          "Nonmetal",
  halogen:           "Halogen",
  noble:             "Noble gas",
  lanthanide:        "Lanthanide",
  actinide:          "Actinide",
  unknown:           "Unknown",
};

// ─── ELEMENTS array ─────────────────────────────────────────────────
// All 118 elements, ordered by atomic number. Each element has the same
// ~22 fields — null when value is unknown or unmeasured.

export const ELEMENTS = [
  {
    number: 1, symbol: "H", name: "Hydrogen", mass: 1.008,
    category: "nonmetal", categoryLabel: "Nonmetal",
    row: 1, col: 1, period: 1, group: 1, block: "s",
    config: "1s1", state: "gas",
    density: 8.99e-05, melt: 14, boil: 20,
    electroneg: 2.20, electronAffinity: 73, ionizationEnergy: 1312, atomicRadius: 53,
    commonCharges: "+1, −1", commonOxidationStates: "+1, −1",
    discovered: 1766, discoveredBy: "Henry Cavendish",
  },
  {
    number: 2, symbol: "He", name: "Helium", mass: 4.003,
    category: "noble", categoryLabel: "Noble gas",
    row: 1, col: 18, period: 1, group: 18, block: "s",
    config: "1s2", state: "gas",
    density: 1.785e-04, melt: null, boil: 4,
    electroneg: null, electronAffinity: null, ionizationEnergy: 2372, atomicRadius: 31,
    commonCharges: "0", commonOxidationStates: "0",
    discovered: 1868, discoveredBy: "Pierre Janssen and Norman Lockyer",
  },
  {
    number: 3, symbol: "Li", name: "Lithium", mass: 6.941,
    category: "alkali", categoryLabel: "Alkali metal",
    row: 2, col: 1, period: 2, group: 1, block: "s",
    config: "[He] 2s1", state: "solid",
    density: 0.535, melt: 454, boil: 1615,
    electroneg: 0.98, electronAffinity: 60, ionizationEnergy: 520, atomicRadius: 167,
    commonCharges: "+1", commonOxidationStates: "+1",
    discovered: 1817, discoveredBy: "Johan August Arfwedson",
  },
  {
    number: 4, symbol: "Be", name: "Beryllium", mass: 9.012,
    category: "alkaline", categoryLabel: "Alkaline earth",
    row: 2, col: 2, period: 2, group: 2, block: "s",
    config: "[He] 2s2", state: "solid",
    density: 1.848, melt: 1560, boil: 2743,
    electroneg: 1.57, electronAffinity: null, ionizationEnergy: 899, atomicRadius: 112,
    commonCharges: "+2", commonOxidationStates: "+2",
    discovered: 1798, discoveredBy: "Louis-Nicolas Vauquelin",
  },
  {
    number: 5, symbol: "B", name: "Boron", mass: 10.811,
    category: "metalloid", categoryLabel: "Metalloid",
    row: 2, col: 13, period: 2, group: 13, block: "p",
    config: "[He] 2s2 2p1", state: "solid",
    density: 2.46, melt: 2348, boil: 4273,
    electroneg: 2.04, electronAffinity: 27, ionizationEnergy: 801, atomicRadius: 87,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1807, discoveredBy: "Humphry Davy, Joseph Louis Gay-Lussac, and Louis Jacques Thénard",
  },
  {
    number: 6, symbol: "C", name: "Carbon", mass: 12.011,
    category: "nonmetal", categoryLabel: "Nonmetal",
    row: 2, col: 14, period: 2, group: 14, block: "p",
    config: "[He] 2s2 2p2", state: "solid",
    density: 2.26, melt: 3823, boil: 4300,
    electroneg: 2.55, electronAffinity: 122, ionizationEnergy: 1086, atomicRadius: 67,
    commonCharges: "+4, −4", commonOxidationStates: "−4, +2, +4",
    discovered: "Ancient", discoveredBy: null,
  },
  {
    number: 7, symbol: "N", name: "Nitrogen", mass: 14.007,
    category: "nonmetal", categoryLabel: "Nonmetal",
    row: 2, col: 15, period: 2, group: 15, block: "p",
    config: "[He] 2s2 2p3", state: "gas",
    density: 1.251e-03, melt: 63, boil: 77,
    electroneg: 3.04, electronAffinity: null, ionizationEnergy: 1402, atomicRadius: 56,
    commonCharges: "−3", commonOxidationStates: "−3, +3, +5",
    discovered: 1772, discoveredBy: "Daniel Rutherford",
  },
  {
    number: 8, symbol: "O", name: "Oxygen", mass: 15.999,
    category: "nonmetal", categoryLabel: "Nonmetal",
    row: 2, col: 16, period: 2, group: 16, block: "p",
    config: "[He] 2s2 2p4", state: "gas",
    density: 1.429e-03, melt: 55, boil: 90,
    electroneg: 3.44, electronAffinity: 141, ionizationEnergy: 1314, atomicRadius: 48,
    commonCharges: "−2", commonOxidationStates: "−2",
    discovered: 1774, discoveredBy: "Carl Wilhelm Scheele and Joseph Priestley",
  },
  {
    number: 9, symbol: "F", name: "Fluorine", mass: 18.998,
    category: "halogen", categoryLabel: "Halogen",
    row: 2, col: 17, period: 2, group: 17, block: "p",
    config: "[He] 2s2 2p5", state: "gas",
    density: 1.696e-03, melt: 54, boil: 85,
    electroneg: 3.98, electronAffinity: 328, ionizationEnergy: 1681, atomicRadius: 42,
    commonCharges: "−1", commonOxidationStates: "−1",
    discovered: 1886, discoveredBy: "Henri Moissan",
  },
  {
    number: 10, symbol: "Ne", name: "Neon", mass: 20.180,
    category: "noble", categoryLabel: "Noble gas",
    row: 2, col: 18, period: 2, group: 18, block: "p",
    config: "[He] 2s2 2p6", state: "gas",
    density: 9.0e-04, melt: 25, boil: 27,
    electroneg: null, electronAffinity: null, ionizationEnergy: 2081, atomicRadius: 38,
    commonCharges: "0", commonOxidationStates: "0",
    discovered: 1898, discoveredBy: "William Ramsay and Morris Travers",
  },
  {
    number: 11, symbol: "Na", name: "Sodium", mass: 22.990,
    category: "alkali", categoryLabel: "Alkali metal",
    row: 3, col: 1, period: 3, group: 1, block: "s",
    config: "[Ne] 3s1", state: "solid",
    density: 0.968, melt: 371, boil: 1156,
    electroneg: 0.93, electronAffinity: 53, ionizationEnergy: 496, atomicRadius: 190,
    commonCharges: "+1", commonOxidationStates: "+1",
    discovered: 1807, discoveredBy: "Humphry Davy",
  },
  {
    number: 12, symbol: "Mg", name: "Magnesium", mass: 24.305,
    category: "alkaline", categoryLabel: "Alkaline earth",
    row: 3, col: 2, period: 3, group: 2, block: "s",
    config: "[Ne] 3s2", state: "solid",
    density: 1.738, melt: 923, boil: 1363,
    electroneg: 1.31, electronAffinity: null, ionizationEnergy: 738, atomicRadius: 145,
    commonCharges: "+2", commonOxidationStates: "+2",
    discovered: 1755, discoveredBy: "Joseph Black",
  },
  {
    number: 13, symbol: "Al", name: "Aluminum", mass: 26.982,
    category: "post-transition", categoryLabel: "Post-transition metal",
    row: 3, col: 13, period: 3, group: 13, block: "p",
    config: "[Ne] 3s2 3p1", state: "solid",
    density: 2.70, melt: 933, boil: 2792,
    electroneg: 1.61, electronAffinity: 43, ionizationEnergy: 578, atomicRadius: 118,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1825, discoveredBy: "Hans Christian Ørsted",
  },
  {
    number: 14, symbol: "Si", name: "Silicon", mass: 28.085,
    category: "metalloid", categoryLabel: "Metalloid",
    row: 3, col: 14, period: 3, group: 14, block: "p",
    config: "[Ne] 3s2 3p2", state: "solid",
    density: 2.33, melt: 1687, boil: 3173,
    electroneg: 1.90, electronAffinity: 134, ionizationEnergy: 786, atomicRadius: 111,
    commonCharges: "+4, −4", commonOxidationStates: "+4, −4",
    discovered: 1824, discoveredBy: "Jöns Jacob Berzelius",
  },
  {
    number: 15, symbol: "P", name: "Phosphorus", mass: 30.974,
    category: "nonmetal", categoryLabel: "Nonmetal",
    row: 3, col: 15, period: 3, group: 15, block: "p",
    config: "[Ne] 3s2 3p3", state: "solid",
    density: 1.823, melt: 317, boil: 554,
    electroneg: 2.19, electronAffinity: 72, ionizationEnergy: 1012, atomicRadius: 98,
    commonCharges: "−3, +3, +5", commonOxidationStates: "−3, +3, +5",
    discovered: 1669, discoveredBy: "Hennig Brand",
  },
  {
    number: 16, symbol: "S", name: "Sulfur", mass: 32.065,
    category: "nonmetal", categoryLabel: "Nonmetal",
    row: 3, col: 16, period: 3, group: 16, block: "p",
    config: "[Ne] 3s2 3p4", state: "solid",
    density: 1.96, melt: 388, boil: 718,
    electroneg: 2.58, electronAffinity: 200, ionizationEnergy: 1000, atomicRadius: 88,
    commonCharges: "−2, +4, +6", commonOxidationStates: "−2, +4, +6",
    discovered: "Ancient", discoveredBy: null,
  },
  {
    number: 17, symbol: "Cl", name: "Chlorine", mass: 35.453,
    category: "halogen", categoryLabel: "Halogen",
    row: 3, col: 17, period: 3, group: 17, block: "p",
    config: "[Ne] 3s2 3p5", state: "gas",
    density: 3.214e-03, melt: 172, boil: 239,
    electroneg: 3.16, electronAffinity: 349, ionizationEnergy: 1251, atomicRadius: 79,
    commonCharges: "−1", commonOxidationStates: "−1, +1, +3, +5, +7",
    discovered: 1774, discoveredBy: "Carl Wilhelm Scheele",
  },
  {
    number: 18, symbol: "Ar", name: "Argon", mass: 39.948,
    category: "noble", categoryLabel: "Noble gas",
    row: 3, col: 18, period: 3, group: 18, block: "p",
    config: "[Ne] 3s2 3p6", state: "gas",
    density: 1.784e-03, melt: 84, boil: 87,
    electroneg: null, electronAffinity: null, ionizationEnergy: 1521, atomicRadius: 71,
    commonCharges: "0", commonOxidationStates: "0",
    discovered: 1894, discoveredBy: "Lord Rayleigh and William Ramsay",
  },
  {
    number: 19, symbol: "K", name: "Potassium", mass: 39.098,
    category: "alkali", categoryLabel: "Alkali metal",
    row: 4, col: 1, period: 4, group: 1, block: "s",
    config: "[Ar] 4s1", state: "solid",
    density: 0.856, melt: 337, boil: 1032,
    electroneg: 0.82, electronAffinity: 48, ionizationEnergy: 419, atomicRadius: 243,
    commonCharges: "+1", commonOxidationStates: "+1",
    discovered: 1807, discoveredBy: "Humphry Davy",
  },
  {
    number: 20, symbol: "Ca", name: "Calcium", mass: 40.078,
    category: "alkaline", categoryLabel: "Alkaline earth",
    row: 4, col: 2, period: 4, group: 2, block: "s",
    config: "[Ar] 4s2", state: "solid",
    density: 1.55, melt: 1115, boil: 1757,
    electroneg: 1.00, electronAffinity: 2, ionizationEnergy: 590, atomicRadius: 194,
    commonCharges: "+2", commonOxidationStates: "+2",
    discovered: 1808, discoveredBy: "Humphry Davy",
  },
  {
    number: 21, symbol: "Sc", name: "Scandium", mass: 44.956,
    category: "transition", categoryLabel: "Transition metal",
    row: 4, col: 3, period: 4, group: 3, block: "d",
    config: "[Ar] 3d1 4s2", state: "solid",
    density: 2.985, melt: 1814, boil: 3103,
    electroneg: 1.36, electronAffinity: 18, ionizationEnergy: 633, atomicRadius: 184,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1879, discoveredBy: "Lars Fredrik Nilson",
  },
  {
    number: 22, symbol: "Ti", name: "Titanium", mass: 47.867,
    category: "transition", categoryLabel: "Transition metal",
    row: 4, col: 4, period: 4, group: 4, block: "d",
    config: "[Ar] 3d2 4s2", state: "solid",
    density: 4.507, melt: 1941, boil: 3560,
    electroneg: 1.54, electronAffinity: 8, ionizationEnergy: 659, atomicRadius: 176,
    commonCharges: "+2, +3, +4", commonOxidationStates: "+2, +3, +4",
    discovered: 1791, discoveredBy: "William Gregor",
  },
  {
    number: 23, symbol: "V", name: "Vanadium", mass: 50.941,
    category: "transition", categoryLabel: "Transition metal",
    row: 4, col: 5, period: 4, group: 5, block: "d",
    config: "[Ar] 3d3 4s2", state: "solid",
    density: 6.11, melt: 2183, boil: 3680,
    electroneg: 1.63, electronAffinity: 51, ionizationEnergy: 651, atomicRadius: 171,
    commonCharges: "+2, +3, +4, +5", commonOxidationStates: "+2, +3, +4, +5",
    discovered: 1801, discoveredBy: "Andrés Manuel del Río",
  },
  {
    number: 24, symbol: "Cr", name: "Chromium", mass: 51.996,
    category: "transition", categoryLabel: "Transition metal",
    row: 4, col: 6, period: 4, group: 6, block: "d",
    config: "[Ar] 3d5 4s1", state: "solid",
    density: 7.14, melt: 2180, boil: 2944,
    electroneg: 1.66, electronAffinity: 65, ionizationEnergy: 653, atomicRadius: 166,
    commonCharges: "+2, +3, +6", commonOxidationStates: "+2, +3, +6",
    discovered: 1797, discoveredBy: "Louis Nicolas Vauquelin",
  },
  {
    number: 25, symbol: "Mn", name: "Manganese", mass: 54.938,
    category: "transition", categoryLabel: "Transition metal",
    row: 4, col: 7, period: 4, group: 7, block: "d",
    config: "[Ar] 3d5 4s2", state: "solid",
    density: 7.47, melt: 1519, boil: 2334,
    electroneg: 1.55, electronAffinity: null, ionizationEnergy: 717, atomicRadius: 161,
    commonCharges: "+2, +3, +4, +7", commonOxidationStates: "+2, +3, +4, +6, +7",
    discovered: 1774, discoveredBy: "Johan Gottlieb Gahn",
  },
  {
    number: 26, symbol: "Fe", name: "Iron", mass: 55.845,
    category: "transition", categoryLabel: "Transition metal",
    row: 4, col: 8, period: 4, group: 8, block: "d",
    config: "[Ar] 3d6 4s2", state: "solid",
    density: 7.874, melt: 1811, boil: 3134,
    electroneg: 1.83, electronAffinity: 15, ionizationEnergy: 763, atomicRadius: 156,
    commonCharges: "+2, +3", commonOxidationStates: "+2, +3",
    discovered: "Ancient", discoveredBy: null,
  },
  {
    number: 27, symbol: "Co", name: "Cobalt", mass: 58.933,
    category: "transition", categoryLabel: "Transition metal",
    row: 4, col: 9, period: 4, group: 9, block: "d",
    config: "[Ar] 3d7 4s2", state: "solid",
    density: 8.90, melt: 1768, boil: 3200,
    electroneg: 1.88, electronAffinity: 64, ionizationEnergy: 760, atomicRadius: 152,
    commonCharges: "+2, +3", commonOxidationStates: "+2, +3",
    discovered: 1735, discoveredBy: "Georg Brandt",
  },
  {
    number: 28, symbol: "Ni", name: "Nickel", mass: 58.693,
    category: "transition", categoryLabel: "Transition metal",
    row: 4, col: 10, period: 4, group: 10, block: "d",
    config: "[Ar] 3d8 4s2", state: "solid",
    density: 8.908, melt: 1728, boil: 3186,
    electroneg: 1.91, electronAffinity: 112, ionizationEnergy: 737, atomicRadius: 149,
    commonCharges: "+2", commonOxidationStates: "+2, +3",
    discovered: 1751, discoveredBy: "Axel Fredrik Cronstedt",
  },
  {
    number: 29, symbol: "Cu", name: "Copper", mass: 63.546,
    category: "transition", categoryLabel: "Transition metal",
    row: 4, col: 11, period: 4, group: 11, block: "d",
    config: "[Ar] 3d10 4s1", state: "solid",
    density: 8.92, melt: 1358, boil: 3200,
    electroneg: 1.90, electronAffinity: 119, ionizationEnergy: 745, atomicRadius: 145,
    commonCharges: "+1, +2", commonOxidationStates: "+1, +2",
    discovered: "Ancient", discoveredBy: null,
  },
  {
    number: 30, symbol: "Zn", name: "Zinc", mass: 65.38,
    category: "transition", categoryLabel: "Transition metal",
    row: 4, col: 12, period: 4, group: 12, block: "d",
    config: "[Ar] 3d10 4s2", state: "solid",
    density: 7.14, melt: 693, boil: 1180,
    electroneg: 1.65, electronAffinity: null, ionizationEnergy: 906, atomicRadius: 142,
    commonCharges: "+2", commonOxidationStates: "+2",
    discovered: "Ancient", discoveredBy: null,
  },
  {
    number: 31, symbol: "Ga", name: "Gallium", mass: 69.723,
    category: "post-transition", categoryLabel: "Post-transition metal",
    row: 4, col: 13, period: 4, group: 13, block: "p",
    config: "[Ar] 3d10 4s2 4p1", state: "solid",
    density: 5.904, melt: 303, boil: 2477,
    electroneg: 1.81, electronAffinity: 41, ionizationEnergy: 579, atomicRadius: 136,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1875, discoveredBy: "Paul-Émile Lecoq de Boisbaudran",
  },
  {
    number: 32, symbol: "Ge", name: "Germanium", mass: 72.64,
    category: "metalloid", categoryLabel: "Metalloid",
    row: 4, col: 14, period: 4, group: 14, block: "p",
    config: "[Ar] 3d10 4s2 4p2", state: "solid",
    density: 5.323, melt: 1211, boil: 3093,
    electroneg: 2.01, electronAffinity: 119, ionizationEnergy: 762, atomicRadius: 125,
    commonCharges: "+2, +4", commonOxidationStates: "+2, +4",
    discovered: 1886, discoveredBy: "Clemens Winkler",
  },
  {
    number: 33, symbol: "As", name: "Arsenic", mass: 74.922,
    category: "metalloid", categoryLabel: "Metalloid",
    row: 4, col: 15, period: 4, group: 15, block: "p",
    config: "[Ar] 3d10 4s2 4p3", state: "solid",
    density: 5.727, melt: 1090, boil: 887,
    electroneg: 2.18, electronAffinity: 78, ionizationEnergy: 947, atomicRadius: 114,
    commonCharges: "−3, +3, +5", commonOxidationStates: "−3, +3, +5",
    discovered: "Ancient", discoveredBy: null,
  },
  {
    number: 34, symbol: "Se", name: "Selenium", mass: 78.96,
    category: "nonmetal", categoryLabel: "Nonmetal",
    row: 4, col: 16, period: 4, group: 16, block: "p",
    config: "[Ar] 3d10 4s2 4p4", state: "solid",
    density: 4.819, melt: 494, boil: 958,
    electroneg: 2.55, electronAffinity: 195, ionizationEnergy: 941, atomicRadius: 103,
    commonCharges: "−2, +4, +6", commonOxidationStates: "−2, +4, +6",
    discovered: 1817, discoveredBy: "Jöns Jacob Berzelius",
  },
  {
    number: 35, symbol: "Br", name: "Bromine", mass: 79.904,
    category: "halogen", categoryLabel: "Halogen",
    row: 4, col: 17, period: 4, group: 17, block: "p",
    config: "[Ar] 3d10 4s2 4p5", state: "liquid",
    density: 3.122, melt: 266, boil: 332,
    electroneg: 2.96, electronAffinity: 325, ionizationEnergy: 1140, atomicRadius: 94,
    commonCharges: "−1", commonOxidationStates: "−1, +1, +3, +5, +7",
    discovered: 1826, discoveredBy: "Antoine Jérôme Balard and Carl Jacob Löwig",
  },
  {
    number: 36, symbol: "Kr", name: "Krypton", mass: 83.798,
    category: "noble", categoryLabel: "Noble gas",
    row: 4, col: 18, period: 4, group: 18, block: "p",
    config: "[Ar] 3d10 4s2 4p6", state: "gas",
    density: 3.749e-03, melt: 116, boil: 120,
    electroneg: 3.00, electronAffinity: null, ionizationEnergy: 1351, atomicRadius: 88,
    commonCharges: "0", commonOxidationStates: "0, +2",
    discovered: 1898, discoveredBy: "William Ramsay and Morris Travers",
  },
  {
    number: 37, symbol: "Rb", name: "Rubidium", mass: 85.468,
    category: "alkali", categoryLabel: "Alkali metal",
    row: 5, col: 1, period: 5, group: 1, block: "s",
    config: "[Kr] 5s1", state: "solid",
    density: 1.532, melt: 312, boil: 961,
    electroneg: 0.82, electronAffinity: 47, ionizationEnergy: 403, atomicRadius: 265,
    commonCharges: "+1", commonOxidationStates: "+1",
    discovered: 1861, discoveredBy: "Robert Bunsen and Gustav Kirchhoff",
  },
  {
    number: 38, symbol: "Sr", name: "Strontium", mass: 87.62,
    category: "alkaline", categoryLabel: "Alkaline earth",
    row: 5, col: 2, period: 5, group: 2, block: "s",
    config: "[Kr] 5s2", state: "solid",
    density: 2.63, melt: 1050, boil: 1655,
    electroneg: 0.95, electronAffinity: 5, ionizationEnergy: 549, atomicRadius: 219,
    commonCharges: "+2", commonOxidationStates: "+2",
    discovered: 1790, discoveredBy: "Adair Crawford",
  },
  {
    number: 39, symbol: "Y", name: "Yttrium", mass: 88.906,
    category: "transition", categoryLabel: "Transition metal",
    row: 5, col: 3, period: 5, group: 3, block: "d",
    config: "[Kr] 4d1 5s2", state: "solid",
    density: 4.472, melt: 1799, boil: 3609,
    electroneg: 1.22, electronAffinity: 30, ionizationEnergy: 600, atomicRadius: 212,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1794, discoveredBy: "Johan Gadolin",
  },
  {
    number: 40, symbol: "Zr", name: "Zirconium", mass: 91.224,
    category: "transition", categoryLabel: "Transition metal",
    row: 5, col: 4, period: 5, group: 4, block: "d",
    config: "[Kr] 4d2 5s2", state: "solid",
    density: 6.511, melt: 2128, boil: 4682,
    electroneg: 1.33, electronAffinity: 41, ionizationEnergy: 640, atomicRadius: 206,
    commonCharges: "+4", commonOxidationStates: "+4",
    discovered: 1789, discoveredBy: "Martin Heinrich Klaproth",
  },
  {
    number: 41, symbol: "Nb", name: "Niobium", mass: 92.906,
    category: "transition", categoryLabel: "Transition metal",
    row: 5, col: 5, period: 5, group: 5, block: "d",
    config: "[Kr] 4d4 5s1", state: "solid",
    density: 8.57, melt: 2750, boil: 5017,
    electroneg: 1.6, electronAffinity: 86, ionizationEnergy: 652, atomicRadius: 198,
    commonCharges: "+3, +5", commonOxidationStates: "+3, +5",
    discovered: 1801, discoveredBy: "Charles Hatchett",
  },
  {
    number: 42, symbol: "Mo", name: "Molybdenum", mass: 95.96,
    category: "transition", categoryLabel: "Transition metal",
    row: 5, col: 6, period: 5, group: 6, block: "d",
    config: "[Kr] 4d5 5s1", state: "solid",
    density: 10.28, melt: 2896, boil: 4912,
    electroneg: 2.16, electronAffinity: 72, ionizationEnergy: 684, atomicRadius: 190,
    commonCharges: "+2, +3, +4, +5, +6", commonOxidationStates: "+2, +3, +4, +5, +6",
    discovered: 1778, discoveredBy: "Carl Wilhelm Scheele",
  },
  {
    number: 43, symbol: "Tc", name: "Technetium", mass: [98],
    category: "transition", categoryLabel: "Transition metal",
    row: 5, col: 7, period: 5, group: 7, block: "d",
    config: "[Kr] 4d5 5s2", state: "solid",
    density: 11.5, melt: 2430, boil: 4538,
    electroneg: 1.9, electronAffinity: 53, ionizationEnergy: 702, atomicRadius: 183,
    commonCharges: "+4, +7", commonOxidationStates: "+4, +7",
    discovered: 1937, discoveredBy: "Emilio Segrè and Carlo Perrier",
  },
  {
    number: 44, symbol: "Ru", name: "Ruthenium", mass: 101.07,
    category: "transition", categoryLabel: "Transition metal",
    row: 5, col: 8, period: 5, group: 8, block: "d",
    config: "[Kr] 4d7 5s1", state: "solid",
    density: 12.37, melt: 2607, boil: 4423,
    electroneg: 2.2, electronAffinity: 101, ionizationEnergy: 710, atomicRadius: 178,
    commonCharges: "+3, +4", commonOxidationStates: "+2, +3, +4, +6, +8",
    discovered: 1844, discoveredBy: "Karl Ernst Claus",
  },
  {
    number: 45, symbol: "Rh", name: "Rhodium", mass: 102.906,
    category: "transition", categoryLabel: "Transition metal",
    row: 5, col: 9, period: 5, group: 9, block: "d",
    config: "[Kr] 4d8 5s1", state: "solid",
    density: 12.45, melt: 2237, boil: 3968,
    electroneg: 2.28, electronAffinity: 110, ionizationEnergy: 720, atomicRadius: 173,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1803, discoveredBy: "William Hyde Wollaston",
  },
  {
    number: 46, symbol: "Pd", name: "Palladium", mass: 106.42,
    category: "transition", categoryLabel: "Transition metal",
    row: 5, col: 10, period: 5, group: 10, block: "d",
    config: "[Kr] 4d10", state: "solid",
    density: 12.023, melt: 1828, boil: 3236,
    electroneg: 2.20, electronAffinity: 54, ionizationEnergy: 804, atomicRadius: 169,
    commonCharges: "+2, +4", commonOxidationStates: "+2, +4",
    discovered: 1803, discoveredBy: "William Hyde Wollaston",
  },
  {
    number: 47, symbol: "Ag", name: "Silver", mass: 107.868,
    category: "transition", categoryLabel: "Transition metal",
    row: 5, col: 11, period: 5, group: 11, block: "d",
    config: "[Kr] 4d10 5s1", state: "solid",
    density: 10.49, melt: 1235, boil: 2435,
    electroneg: 1.93, electronAffinity: 126, ionizationEnergy: 731, atomicRadius: 165,
    commonCharges: "+1", commonOxidationStates: "+1",
    discovered: "Ancient", discoveredBy: null,
  },
  {
    number: 48, symbol: "Cd", name: "Cadmium", mass: 112.411,
    category: "transition", categoryLabel: "Transition metal",
    row: 5, col: 12, period: 5, group: 12, block: "d",
    config: "[Kr] 4d10 5s2", state: "solid",
    density: 8.65, melt: 594, boil: 1040,
    electroneg: 1.69, electronAffinity: null, ionizationEnergy: 868, atomicRadius: 161,
    commonCharges: "+2", commonOxidationStates: "+2",
    discovered: 1817, discoveredBy: "Friedrich Stromeyer",
  },
  {
    number: 49, symbol: "In", name: "Indium", mass: 114.818,
    category: "post-transition", categoryLabel: "Post-transition metal",
    row: 5, col: 13, period: 5, group: 13, block: "p",
    config: "[Kr] 4d10 5s2 5p1", state: "solid",
    density: 7.31, melt: 430, boil: 2345,
    electroneg: 1.78, electronAffinity: 39, ionizationEnergy: 558, atomicRadius: 156,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1863, discoveredBy: "Ferdinand Reich and Theodor Richter",
  },
  {
    number: 50, symbol: "Sn", name: "Tin", mass: 118.710,
    category: "post-transition", categoryLabel: "Post-transition metal",
    row: 5, col: 14, period: 5, group: 14, block: "p",
    config: "[Kr] 4d10 5s2 5p2", state: "solid",
    density: 7.31, melt: 505, boil: 2875,
    electroneg: 1.96, electronAffinity: 107, ionizationEnergy: 709, atomicRadius: 145,
    commonCharges: "+2, +4", commonOxidationStates: "+2, +4",
    discovered: "Ancient", discoveredBy: null,
  },
  {
    number: 51, symbol: "Sb", name: "Antimony", mass: 121.760,
    category: "metalloid", categoryLabel: "Metalloid",
    row: 5, col: 15, period: 5, group: 15, block: "p",
    config: "[Kr] 4d10 5s2 5p3", state: "solid",
    density: 6.685, melt: 904, boil: 1860,
    electroneg: 2.05, electronAffinity: 101, ionizationEnergy: 834, atomicRadius: 133,
    commonCharges: "−3, +3, +5", commonOxidationStates: "−3, +3, +5",
    discovered: "Ancient", discoveredBy: null,
  },
  {
    number: 52, symbol: "Te", name: "Tellurium", mass: 127.60,
    category: "metalloid", categoryLabel: "Metalloid",
    row: 5, col: 16, period: 5, group: 16, block: "p",
    config: "[Kr] 4d10 5s2 5p4", state: "solid",
    density: 6.232, melt: 723, boil: 1261,
    electroneg: 2.1, electronAffinity: 190, ionizationEnergy: 869, atomicRadius: 123,
    commonCharges: "−2, +4, +6", commonOxidationStates: "−2, +4, +6",
    discovered: 1782, discoveredBy: "Franz-Joseph Müller von Reichenstein",
  },
  {
    number: 53, symbol: "I", name: "Iodine", mass: 126.904,
    category: "halogen", categoryLabel: "Halogen",
    row: 5, col: 17, period: 5, group: 17, block: "p",
    config: "[Kr] 4d10 5s2 5p5", state: "solid",
    density: 4.93, melt: 387, boil: 457,
    electroneg: 2.66, electronAffinity: 295, ionizationEnergy: 1008, atomicRadius: 115,
    commonCharges: "−1", commonOxidationStates: "−1, +1, +3, +5, +7",
    discovered: 1811, discoveredBy: "Bernard Courtois",
  },
  {
    number: 54, symbol: "Xe", name: "Xenon", mass: 131.293,
    category: "noble", categoryLabel: "Noble gas",
    row: 5, col: 18, period: 5, group: 18, block: "p",
    config: "[Kr] 4d10 5s2 5p6", state: "gas",
    density: 5.894e-03, melt: 161, boil: 165,
    electroneg: 2.60, electronAffinity: null, ionizationEnergy: 1170, atomicRadius: 108,
    commonCharges: "0", commonOxidationStates: "0, +2, +4, +6, +8",
    discovered: 1898, discoveredBy: "William Ramsay and Morris Travers",
  },
  {
    number: 55, symbol: "Cs", name: "Cesium", mass: 132.905,
    category: "alkali", categoryLabel: "Alkali metal",
    row: 6, col: 1, period: 6, group: 1, block: "s",
    config: "[Xe] 6s1", state: "solid",
    density: 1.873, melt: 302, boil: 944,
    electroneg: 0.79, electronAffinity: 46, ionizationEnergy: 376, atomicRadius: 298,
    commonCharges: "+1", commonOxidationStates: "+1",
    discovered: 1860, discoveredBy: "Robert Bunsen and Gustav Kirchhoff",
  },
  {
    number: 56, symbol: "Ba", name: "Barium", mass: 137.327,
    category: "alkaline", categoryLabel: "Alkaline earth",
    row: 6, col: 2, period: 6, group: 2, block: "s",
    config: "[Xe] 6s2", state: "solid",
    density: 3.594, melt: 1000, boil: 2170,
    electroneg: 0.89, electronAffinity: 14, ionizationEnergy: 503, atomicRadius: 253,
    commonCharges: "+2", commonOxidationStates: "+2",
    discovered: 1808, discoveredBy: "Humphry Davy",
  },
  // ─── Lanthanides (57-71) ─── period 6, f-block, group: null ─────
  {
    number: 57, symbol: "La", name: "Lanthanum", mass: 138.905,
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 3, period: 6, group: null, block: "f",
    config: "[Xe] 5d1 6s2", state: "solid",
    density: 6.145, melt: 1193, boil: 3737,
    electroneg: 1.10, electronAffinity: 48, ionizationEnergy: 538, atomicRadius: 240,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1839, discoveredBy: "Carl Gustaf Mosander",
  },
  {
    number: 58, symbol: "Ce", name: "Cerium", mass: 140.116,
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 4, period: 6, group: null, block: "f",
    config: "[Xe] 4f1 5d1 6s2", state: "solid",
    density: 6.770, melt: 1068, boil: 3716,
    electroneg: 1.12, electronAffinity: 50, ionizationEnergy: 534, atomicRadius: 235,
    commonCharges: "+3, +4", commonOxidationStates: "+3, +4",
    discovered: 1803, discoveredBy: "Jöns Jakob Berzelius and Wilhelm Hisinger",
  },
  {
    number: 59, symbol: "Pr", name: "Praseodymium", mass: 140.908,
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 5, period: 6, group: null, block: "f",
    config: "[Xe] 4f3 6s2", state: "solid",
    density: 6.773, melt: 1208, boil: 3793,
    electroneg: 1.13, electronAffinity: 50, ionizationEnergy: 527, atomicRadius: 239,
    commonCharges: "+3", commonOxidationStates: "+3, +4",
    discovered: 1885, discoveredBy: "Carl Auer von Welsbach",
  },
  {
    number: 60, symbol: "Nd", name: "Neodymium", mass: 144.242,
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 6, period: 6, group: null, block: "f",
    config: "[Xe] 4f4 6s2", state: "solid",
    density: 7.007, melt: 1297, boil: 3347,
    electroneg: 1.14, electronAffinity: 50, ionizationEnergy: 533, atomicRadius: 229,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1885, discoveredBy: "Carl Auer von Welsbach",
  },
  {
    number: 61, symbol: "Pm", name: "Promethium", mass: [145],
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 7, period: 6, group: null, block: "f",
    config: "[Xe] 4f5 6s2", state: "solid",
    density: 7.26, melt: 1315, boil: 3273,
    electroneg: 1.13, electronAffinity: 50, ionizationEnergy: 540, atomicRadius: 236,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1945, discoveredBy: "Jacob A. Marinsky, Lawrence E. Glendenin, and Charles D. Coryell",
  },
  {
    number: 62, symbol: "Sm", name: "Samarium", mass: 150.36,
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 8, period: 6, group: null, block: "f",
    config: "[Xe] 4f6 6s2", state: "solid",
    density: 7.52, melt: 1345, boil: 2067,
    electroneg: 1.17, electronAffinity: 50, ionizationEnergy: 545, atomicRadius: 229,
    commonCharges: "+2, +3", commonOxidationStates: "+2, +3",
    discovered: 1879, discoveredBy: "Paul-Émile Lecoq de Boisbaudran",
  },
  {
    number: 63, symbol: "Eu", name: "Europium", mass: 151.964,
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 9, period: 6, group: null, block: "f",
    config: "[Xe] 4f7 6s2", state: "solid",
    density: 5.243, melt: 1099, boil: 1802,
    electroneg: 1.20, electronAffinity: 50, ionizationEnergy: 547, atomicRadius: 233,
    commonCharges: "+2, +3", commonOxidationStates: "+2, +3",
    discovered: 1901, discoveredBy: "Eugène-Anatole Demarçay",
  },
  {
    number: 64, symbol: "Gd", name: "Gadolinium", mass: 157.25,
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 10, period: 6, group: null, block: "f",
    config: "[Xe] 4f7 5d1 6s2", state: "solid",
    density: 7.895, melt: 1585, boil: 3546,
    electroneg: 1.20, electronAffinity: 50, ionizationEnergy: 593, atomicRadius: 237,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1880, discoveredBy: "Jean Charles Galissard de Marignac",
  },
  {
    number: 65, symbol: "Tb", name: "Terbium", mass: 158.925,
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 11, period: 6, group: null, block: "f",
    config: "[Xe] 4f9 6s2", state: "solid",
    density: 8.229, melt: 1629, boil: 3503,
    electroneg: 1.20, electronAffinity: 50, ionizationEnergy: 566, atomicRadius: 221,
    commonCharges: "+3, +4", commonOxidationStates: "+3, +4",
    discovered: 1843, discoveredBy: "Carl Gustaf Mosander",
  },
  {
    number: 66, symbol: "Dy", name: "Dysprosium", mass: 162.500,
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 12, period: 6, group: null, block: "f",
    config: "[Xe] 4f10 6s2", state: "solid",
    density: 8.55, melt: 1680, boil: 2840,
    electroneg: 1.22, electronAffinity: 50, ionizationEnergy: 573, atomicRadius: 229,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1886, discoveredBy: "Paul-Émile Lecoq de Boisbaudran",
  },
  {
    number: 67, symbol: "Ho", name: "Holmium", mass: 164.930,
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 13, period: 6, group: null, block: "f",
    config: "[Xe] 4f11 6s2", state: "solid",
    density: 8.795, melt: 1734, boil: 2993,
    electroneg: 1.23, electronAffinity: 50, ionizationEnergy: 581, atomicRadius: 216,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1878, discoveredBy: "Marc Delafontaine and Jacques-Louis Soret",
  },
  {
    number: 68, symbol: "Er", name: "Erbium", mass: 167.259,
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 14, period: 6, group: null, block: "f",
    config: "[Xe] 4f12 6s2", state: "solid",
    density: 9.066, melt: 1802, boil: 3141,
    electroneg: 1.24, electronAffinity: 50, ionizationEnergy: 589, atomicRadius: 235,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1843, discoveredBy: "Carl Gustaf Mosander",
  },
  {
    number: 69, symbol: "Tm", name: "Thulium", mass: 168.934,
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 15, period: 6, group: null, block: "f",
    config: "[Xe] 4f13 6s2", state: "solid",
    density: 9.321, melt: 1818, boil: 2223,
    electroneg: 1.25, electronAffinity: 99, ionizationEnergy: 597, atomicRadius: 227,
    commonCharges: "+3", commonOxidationStates: "+2, +3",
    discovered: 1879, discoveredBy: "Per Teodor Cleve",
  },
  {
    number: 70, symbol: "Yb", name: "Ytterbium", mass: 173.054,
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 16, period: 6, group: null, block: "f",
    config: "[Xe] 4f14 6s2", state: "solid",
    density: 6.965, melt: 1097, boil: 1469,
    electroneg: 1.10, electronAffinity: null, ionizationEnergy: 603, atomicRadius: 242,
    commonCharges: "+2, +3", commonOxidationStates: "+2, +3",
    discovered: 1878, discoveredBy: "Jean Charles Galissard de Marignac",
  },
  {
    number: 71, symbol: "Lu", name: "Lutetium", mass: 174.967,
    category: "lanthanide", categoryLabel: "Lanthanide",
    row: 9, col: 17, period: 6, group: null, block: "f",
    config: "[Xe] 4f14 5d1 6s2", state: "solid",
    density: 9.840, melt: 1925, boil: 3675,
    electroneg: 1.27, electronAffinity: 33, ionizationEnergy: 524, atomicRadius: 221,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1907, discoveredBy: "Georges Urbain, Carl Auer von Welsbach, and Charles James",
  },
  // ─── d-block period 6 (72-80) ─────────────────────────────────
  {
    number: 72, symbol: "Hf", name: "Hafnium", mass: 178.49,
    category: "transition", categoryLabel: "Transition metal",
    row: 6, col: 4, period: 6, group: 4, block: "d",
    config: "[Xe] 4f14 5d2 6s2", state: "solid",
    density: 13.31, melt: 2506, boil: 4876,
    electroneg: 1.30, electronAffinity: 0, ionizationEnergy: 659, atomicRadius: 212,
    commonCharges: "+4", commonOxidationStates: "+4",
    discovered: 1923, discoveredBy: "Dirk Coster and Georg von Hevesy",
  },
  {
    number: 73, symbol: "Ta", name: "Tantalum", mass: 180.948,
    category: "transition", categoryLabel: "Transition metal",
    row: 6, col: 5, period: 6, group: 5, block: "d",
    config: "[Xe] 4f14 5d3 6s2", state: "solid",
    density: 16.654, melt: 3290, boil: 5731,
    electroneg: 1.5, electronAffinity: 31, ionizationEnergy: 761, atomicRadius: 217,
    commonCharges: "+5", commonOxidationStates: "+5",
    discovered: 1802, discoveredBy: "Anders Gustaf Ekeberg",
  },
  {
    number: 74, symbol: "W", name: "Tungsten", mass: 183.84,
    category: "transition", categoryLabel: "Transition metal",
    row: 6, col: 6, period: 6, group: 6, block: "d",
    config: "[Xe] 4f14 5d4 6s2", state: "solid",
    density: 19.25, melt: 3695, boil: 5828,
    electroneg: 2.36, electronAffinity: 79, ionizationEnergy: 770, atomicRadius: 210,
    commonCharges: "+4, +6", commonOxidationStates: "+4, +6",
    discovered: 1783, discoveredBy: "Juan José and Fausto Elhuyar",
  },
  {
    number: 75, symbol: "Re", name: "Rhenium", mass: 186.207,
    category: "transition", categoryLabel: "Transition metal",
    row: 6, col: 7, period: 6, group: 7, block: "d",
    config: "[Xe] 4f14 5d5 6s2", state: "solid",
    density: 21.02, melt: 3459, boil: 5869,
    electroneg: 1.9, electronAffinity: 15, ionizationEnergy: 760, atomicRadius: 217,
    commonCharges: "+4, +7", commonOxidationStates: "+4, +7",
    discovered: 1925, discoveredBy: "Walter Noddack, Ida Tacke, and Otto Berg",
  },
  {
    number: 76, symbol: "Os", name: "Osmium", mass: 190.23,
    category: "transition", categoryLabel: "Transition metal",
    row: 6, col: 8, period: 6, group: 8, block: "d",
    config: "[Xe] 4f14 5d6 6s2", state: "solid",
    density: 22.61, melt: 3306, boil: 5285,
    electroneg: 2.2, electronAffinity: 106, ionizationEnergy: 840, atomicRadius: 216,
    commonCharges: "+3, +4, +8", commonOxidationStates: "+2, +3, +4, +6, +8",
    discovered: 1803, discoveredBy: "Smithson Tennant",
  },
  {
    number: 77, symbol: "Ir", name: "Iridium", mass: 192.217,
    category: "transition", categoryLabel: "Transition metal",
    row: 6, col: 9, period: 6, group: 9, block: "d",
    config: "[Xe] 4f14 5d7 6s2", state: "solid",
    density: 22.65, melt: 2719, boil: 4701,
    electroneg: 2.20, electronAffinity: 151, ionizationEnergy: 880, atomicRadius: 202,
    commonCharges: "+3, +4", commonOxidationStates: "+3, +4",
    discovered: 1803, discoveredBy: "Smithson Tennant",
  },
  {
    number: 78, symbol: "Pt", name: "Platinum", mass: 195.084,
    category: "transition", categoryLabel: "Transition metal",
    row: 6, col: 10, period: 6, group: 10, block: "d",
    config: "[Xe] 4f14 5d9 6s1", state: "solid",
    density: 21.45, melt: 2041, boil: 4098,
    electroneg: 2.28, electronAffinity: 205, ionizationEnergy: 870, atomicRadius: 209,
    commonCharges: "+2, +4", commonOxidationStates: "+2, +4",
    discovered: 1735, discoveredBy: "Antonio de Ulloa",
  },
  {
    number: 79, symbol: "Au", name: "Gold", mass: 196.967,
    category: "transition", categoryLabel: "Transition metal",
    row: 6, col: 11, period: 6, group: 11, block: "d",
    config: "[Xe] 4f14 5d10 6s1", state: "solid",
    density: 19.32, melt: 1337, boil: 3129,
    electroneg: 2.54, electronAffinity: 223, ionizationEnergy: 890, atomicRadius: 166,
    commonCharges: "+1, +3", commonOxidationStates: "+1, +3",
    discovered: "Ancient", discoveredBy: null,
  },
  {
    number: 80, symbol: "Hg", name: "Mercury", mass: 200.59,
    category: "transition", categoryLabel: "Transition metal",
    row: 6, col: 12, period: 6, group: 12, block: "d",
    config: "[Xe] 4f14 5d10 6s2", state: "liquid",
    density: 13.534, melt: 234, boil: 630,
    electroneg: 2.00, electronAffinity: null, ionizationEnergy: 1007, atomicRadius: 209,
    commonCharges: "+1, +2", commonOxidationStates: "+1, +2",
    discovered: "Ancient", discoveredBy: null,
  },
  // ─── p-block period 6 (81-86) ─────────────────────────────────
  {
    number: 81, symbol: "Tl", name: "Thallium", mass: 204.383,
    category: "post-transition", categoryLabel: "Post-transition metal",
    row: 6, col: 13, period: 6, group: 13, block: "p",
    config: "[Xe] 4f14 5d10 6s2 6p1", state: "solid",
    density: 11.85, melt: 577, boil: 1746,
    electroneg: 1.62, electronAffinity: 36, ionizationEnergy: 589, atomicRadius: 156,
    commonCharges: "+1, +3", commonOxidationStates: "+1, +3",
    discovered: 1861, discoveredBy: "William Crookes",
  },
  {
    number: 82, symbol: "Pb", name: "Lead", mass: 207.2,
    category: "post-transition", categoryLabel: "Post-transition metal",
    row: 6, col: 14, period: 6, group: 14, block: "p",
    config: "[Xe] 4f14 5d10 6s2 6p2", state: "solid",
    density: 11.34, melt: 600, boil: 2022,
    electroneg: 2.33, electronAffinity: 35, ionizationEnergy: 716, atomicRadius: 154,
    commonCharges: "+2, +4", commonOxidationStates: "+2, +4",
    discovered: "Ancient", discoveredBy: null,
  },
  {
    number: 83, symbol: "Bi", name: "Bismuth", mass: 208.980,
    category: "post-transition", categoryLabel: "Post-transition metal",
    row: 6, col: 15, period: 6, group: 15, block: "p",
    config: "[Xe] 4f14 5d10 6s2 6p3", state: "solid",
    density: 9.78, melt: 544, boil: 1837,
    electroneg: 2.02, electronAffinity: 91, ionizationEnergy: 703, atomicRadius: 143,
    commonCharges: "+3, +5", commonOxidationStates: "+3, +5",
    discovered: "Ancient", discoveredBy: null,
  },
  {
    number: 84, symbol: "Po", name: "Polonium", mass: [209],
    category: "metalloid", categoryLabel: "Metalloid",
    row: 6, col: 16, period: 6, group: 16, block: "p",
    config: "[Xe] 4f14 5d10 6s2 6p4", state: "solid",
    density: 9.196, melt: 527, boil: 1235,
    electroneg: 2.0, electronAffinity: 183, ionizationEnergy: 812, atomicRadius: 135,
    commonCharges: "+2, +4", commonOxidationStates: "−2, +2, +4",
    discovered: 1898, discoveredBy: "Pierre and Marie Curie",
  },
  {
    number: 85, symbol: "At", name: "Astatine", mass: [210],
    category: "halogen", categoryLabel: "Halogen",
    row: 6, col: 17, period: 6, group: 17, block: "p",
    config: "[Xe] 4f14 5d10 6s2 6p5", state: "solid",
    density: null, melt: 575, boil: null,
    electroneg: 2.2, electronAffinity: 270, ionizationEnergy: 920, atomicRadius: 127,
    commonCharges: "−1", commonOxidationStates: "−1, +1, +3, +5, +7",
    discovered: 1940, discoveredBy: "Dale R. Corson, Kenneth Ross MacKenzie, and Emilio Segrè",
  },
  {
    number: 86, symbol: "Rn", name: "Radon", mass: [222],
    category: "noble", categoryLabel: "Noble gas",
    row: 6, col: 18, period: 6, group: 18, block: "p",
    config: "[Xe] 4f14 5d10 6s2 6p6", state: "gas",
    density: 9.73e-03, melt: 202, boil: 211,
    electroneg: 2.2, electronAffinity: null, ionizationEnergy: 1037, atomicRadius: 120,
    commonCharges: "0", commonOxidationStates: "0, +2",
    discovered: 1899, discoveredBy: "Friedrich Ernst Dorn",
  },
  {
    number: 87, symbol: "Fr", name: "Francium", mass: [223],
    category: "alkali", categoryLabel: "Alkali metal",
    row: 7, col: 1, period: 7, group: 1, block: "s",
    config: "[Rn] 7s1", state: "solid",
    density: null, melt: 300, boil: 950,
    electroneg: 0.7, electronAffinity: 47, ionizationEnergy: 380, atomicRadius: null,
    commonCharges: "+1", commonOxidationStates: "+1",
    discovered: 1939, discoveredBy: "Marguerite Perey",
  },
  {
    number: 88, symbol: "Ra", name: "Radium", mass: [226],
    category: "alkaline", categoryLabel: "Alkaline earth",
    row: 7, col: 2, period: 7, group: 2, block: "s",
    config: "[Rn] 7s2", state: "solid",
    density: 5.0, melt: 973, boil: 2010,
    electroneg: 0.9, electronAffinity: 10, ionizationEnergy: 509, atomicRadius: null,
    commonCharges: "+2", commonOxidationStates: "+2",
    discovered: 1898, discoveredBy: "Pierre and Marie Curie",
  },
  // ─── Actinides (89-103) ─── period 7, f-block, group: null ─────
  {
    number: 89, symbol: "Ac", name: "Actinium", mass: [227],
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 3, period: 7, group: null, block: "f",
    config: "[Rn] 6d1 7s2", state: "solid",
    density: 10.07, melt: 1323, boil: 3473,
    electroneg: 1.1, electronAffinity: 34, ionizationEnergy: 499, atomicRadius: 260,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1899, discoveredBy: "André-Louis Debierne",
  },
  {
    number: 90, symbol: "Th", name: "Thorium", mass: 232.038,
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 4, period: 7, group: null, block: "f",
    config: "[Rn] 6d2 7s2", state: "solid",
    density: 11.724, melt: 2023, boil: 5093,
    electroneg: 1.3, electronAffinity: 113, ionizationEnergy: 587, atomicRadius: 237,
    commonCharges: "+4", commonOxidationStates: "+4",
    discovered: 1829, discoveredBy: "Jöns Jakob Berzelius",
  },
  {
    number: 91, symbol: "Pa", name: "Protactinium", mass: 231.036,
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 5, period: 7, group: null, block: "f",
    config: "[Rn] 5f2 6d1 7s2", state: "solid",
    density: 15.37, melt: 1845, boil: 4273,
    electroneg: 1.5, electronAffinity: 53, ionizationEnergy: 568, atomicRadius: 243,
    commonCharges: "+5", commonOxidationStates: "+4, +5",
    discovered: 1913, discoveredBy: "Kazimierz Fajans and Oswald Helmuth Göhring",
  },
  {
    number: 92, symbol: "U", name: "Uranium", mass: 238.029,
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 6, period: 7, group: null, block: "f",
    config: "[Rn] 5f3 6d1 7s2", state: "solid",
    density: 19.05, melt: 1408, boil: 4200,
    electroneg: 1.38, electronAffinity: 51, ionizationEnergy: 597, atomicRadius: 240,
    commonCharges: "+4, +6", commonOxidationStates: "+3, +4, +5, +6",
    discovered: 1789, discoveredBy: "Martin Heinrich Klaproth",
  },
  {
    number: 93, symbol: "Np", name: "Neptunium", mass: [237],
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 7, period: 7, group: null, block: "f",
    config: "[Rn] 5f4 6d1 7s2", state: "solid",
    density: 20.45, melt: 917, boil: 4273,
    electroneg: 1.36, electronAffinity: 45, ionizationEnergy: 604, atomicRadius: 221,
    commonCharges: "+5", commonOxidationStates: "+3, +4, +5, +6",
    discovered: 1940, discoveredBy: "Edwin McMillan and Philip H. Abelson",
  },
  {
    number: 94, symbol: "Pu", name: "Plutonium", mass: [244],
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 8, period: 7, group: null, block: "f",
    config: "[Rn] 5f6 7s2", state: "solid",
    density: 19.816, melt: 913, boil: 3503,
    electroneg: 1.28, electronAffinity: null, ionizationEnergy: 585, atomicRadius: 243,
    commonCharges: "+4", commonOxidationStates: "+3, +4, +5, +6",
    discovered: 1940, discoveredBy: "Glenn T. Seaborg, Joseph W. Kennedy, Edwin McMillan, and Arthur Wahl",
  },
  {
    number: 95, symbol: "Am", name: "Americium", mass: [243],
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 9, period: 7, group: null, block: "f",
    config: "[Rn] 5f7 7s2", state: "solid",
    density: 13.69, melt: 1449, boil: 2284,
    electroneg: 1.3, electronAffinity: 10, ionizationEnergy: 578, atomicRadius: 244,
    commonCharges: "+3", commonOxidationStates: "+3, +4, +5, +6",
    discovered: 1944, discoveredBy: "Glenn T. Seaborg and collaborators",
  },
  {
    number: 96, symbol: "Cm", name: "Curium", mass: [247],
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 10, period: 7, group: null, block: "f",
    config: "[Rn] 5f7 6d1 7s2", state: "solid",
    density: 13.51, melt: 1618, boil: 3383,
    electroneg: 1.3, electronAffinity: 27, ionizationEnergy: 581, atomicRadius: 245,
    commonCharges: "+3", commonOxidationStates: "+3, +4",
    discovered: 1944, discoveredBy: "Glenn T. Seaborg and collaborators",
  },
  {
    number: 97, symbol: "Bk", name: "Berkelium", mass: [247],
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 11, period: 7, group: null, block: "f",
    config: "[Rn] 5f9 7s2", state: "solid",
    density: 14.78, melt: 1323, boil: null,
    electroneg: 1.3, electronAffinity: null, ionizationEnergy: 601, atomicRadius: 244,
    commonCharges: "+3", commonOxidationStates: "+3, +4",
    discovered: 1949, discoveredBy: "Glenn T. Seaborg and collaborators",
  },
  {
    number: 98, symbol: "Cf", name: "Californium", mass: [251],
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 12, period: 7, group: null, block: "f",
    config: "[Rn] 5f10 7s2", state: "solid",
    density: 15.1, melt: 1173, boil: null,
    electroneg: 1.3, electronAffinity: null, ionizationEnergy: 608, atomicRadius: 245,
    commonCharges: "+3", commonOxidationStates: "+2, +3, +4",
    discovered: 1950, discoveredBy: "Glenn T. Seaborg and collaborators",
  },
  {
    number: 99, symbol: "Es", name: "Einsteinium", mass: [252],
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 13, period: 7, group: null, block: "f",
    config: "[Rn] 5f11 7s2", state: "solid",
    density: 8.84, melt: 1133, boil: null,
    electroneg: 1.3, electronAffinity: null, ionizationEnergy: 619, atomicRadius: 245,
    commonCharges: "+3", commonOxidationStates: "+2, +3",
    discovered: 1952, discoveredBy: "Albert Ghiorso and collaborators",
  },
  {
    number: 100, symbol: "Fm", name: "Fermium", mass: [257],
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 14, period: 7, group: null, block: "f",
    config: "[Rn] 5f12 7s2", state: "unknown",
    density: null, melt: 1800, boil: null,
    electroneg: 1.3, electronAffinity: null, ionizationEnergy: 627, atomicRadius: null,
    commonCharges: "+3", commonOxidationStates: "+2, +3",
    discovered: 1952, discoveredBy: "Albert Ghiorso and collaborators",
  },
  {
    number: 101, symbol: "Md", name: "Mendelevium", mass: [258],
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 15, period: 7, group: null, block: "f",
    config: "[Rn] 5f13 7s2", state: "unknown",
    density: null, melt: 1100, boil: null,
    electroneg: 1.3, electronAffinity: null, ionizationEnergy: 635, atomicRadius: null,
    commonCharges: "+3", commonOxidationStates: "+2, +3",
    discovered: 1955, discoveredBy: "Albert Ghiorso, Glenn T. Seaborg, and collaborators",
  },
  {
    number: 102, symbol: "No", name: "Nobelium", mass: [259],
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 16, period: 7, group: null, block: "f",
    config: "[Rn] 5f14 7s2", state: "unknown",
    density: null, melt: 1100, boil: null,
    electroneg: 1.3, electronAffinity: null, ionizationEnergy: 642, atomicRadius: null,
    commonCharges: "+2", commonOxidationStates: "+2, +3",
    discovered: 1966, discoveredBy: "Joint Institute for Nuclear Research (Dubna)",
  },
  {
    number: 103, symbol: "Lr", name: "Lawrencium", mass: [262],
    category: "actinide", categoryLabel: "Actinide",
    row: 10, col: 17, period: 7, group: null, block: "f",
    config: "[Rn] 5f14 7s2 7p1", state: "unknown",
    density: null, melt: 1900, boil: null,
    electroneg: 1.3, electronAffinity: null, ionizationEnergy: 470, atomicRadius: null,
    commonCharges: "+3", commonOxidationStates: "+3",
    discovered: 1961, discoveredBy: "Albert Ghiorso and collaborators",
  },
  // ─── d-block period 7 (104-112) ─── transactinides ─────────────
  {
    number: 104, symbol: "Rf", name: "Rutherfordium", mass: [267],
    category: "transition", categoryLabel: "Transition metal",
    row: 7, col: 4, period: 7, group: 4, block: "d",
    config: "[Rn] 5f14 6d2 7s2", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 580, atomicRadius: null,
    commonCharges: "+4", commonOxidationStates: "+4",
    discovered: 1964, discoveredBy: "Joint Institute for Nuclear Research (Dubna)",
  },
  {
    number: 105, symbol: "Db", name: "Dubnium", mass: [268],
    category: "transition", categoryLabel: "Transition metal",
    row: 7, col: 5, period: 7, group: 5, block: "d",
    config: "[Rn] 5f14 6d3 7s2", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 665, atomicRadius: null,
    commonCharges: "+5", commonOxidationStates: "+5",
    discovered: 1968, discoveredBy: "Joint Institute for Nuclear Research (Dubna)",
  },
  {
    number: 106, symbol: "Sg", name: "Seaborgium", mass: [271],
    category: "transition", categoryLabel: "Transition metal",
    row: 7, col: 6, period: 7, group: 6, block: "d",
    config: "[Rn] 5f14 6d4 7s2", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 757, atomicRadius: null,
    commonCharges: "+6", commonOxidationStates: "+6",
    discovered: 1974, discoveredBy: "Lawrence Berkeley Laboratory",
  },
  {
    number: 107, symbol: "Bh", name: "Bohrium", mass: [272],
    category: "transition", categoryLabel: "Transition metal",
    row: 7, col: 7, period: 7, group: 7, block: "d",
    config: "[Rn] 5f14 6d5 7s2", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 740, atomicRadius: null,
    commonCharges: "+7", commonOxidationStates: "+7",
    discovered: 1981, discoveredBy: "Gesellschaft für Schwerionenforschung",
  },
  {
    number: 108, symbol: "Hs", name: "Hassium", mass: [270],
    category: "transition", categoryLabel: "Transition metal",
    row: 7, col: 8, period: 7, group: 8, block: "d",
    config: "[Rn] 5f14 6d6 7s2", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 730, atomicRadius: null,
    commonCharges: "+8", commonOxidationStates: "+8",
    discovered: 1984, discoveredBy: "Gesellschaft für Schwerionenforschung",
  },
  {
    number: 109, symbol: "Mt", name: "Meitnerium", mass: [276],
    category: "transition", categoryLabel: "Transition metal",
    row: 7, col: 9, period: 7, group: 9, block: "d",
    config: "[Rn] 5f14 6d7 7s2", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 800, atomicRadius: null,
    commonCharges: null, commonOxidationStates: null,
    discovered: 1982, discoveredBy: "Gesellschaft für Schwerionenforschung",
  },
  {
    number: 110, symbol: "Ds", name: "Darmstadtium", mass: [281],
    category: "transition", categoryLabel: "Transition metal",
    row: 7, col: 10, period: 7, group: 10, block: "d",
    config: "[Rn] 5f14 6d9 7s1", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 960, atomicRadius: null,
    commonCharges: null, commonOxidationStates: null,
    discovered: 1994, discoveredBy: "Gesellschaft für Schwerionenforschung",
  },
  {
    number: 111, symbol: "Rg", name: "Roentgenium", mass: [280],
    category: "transition", categoryLabel: "Transition metal",
    row: 7, col: 11, period: 7, group: 11, block: "d",
    config: "[Rn] 5f14 6d10 7s1", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 1020, atomicRadius: null,
    commonCharges: null, commonOxidationStates: null,
    discovered: 1994, discoveredBy: "Gesellschaft für Schwerionenforschung",
  },
  {
    number: 112, symbol: "Cn", name: "Copernicium", mass: [285],
    category: "transition", categoryLabel: "Transition metal",
    row: 7, col: 12, period: 7, group: 12, block: "d",
    config: "[Rn] 5f14 6d10 7s2", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 1155, atomicRadius: null,
    commonCharges: null, commonOxidationStates: null,
    discovered: 1996, discoveredBy: "Gesellschaft für Schwerionenforschung",
  },
  // ─── p-block period 7 (113-118) ─── superheavies ────────────────
  {
    number: 113, symbol: "Nh", name: "Nihonium", mass: [284],
    category: "post-transition", categoryLabel: "Post-transition metal",
    row: 7, col: 13, period: 7, group: 13, block: "p",
    config: "[Rn] 5f14 6d10 7s2 7p1", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 707, atomicRadius: null,
    commonCharges: null, commonOxidationStates: null,
    discovered: 2003, discoveredBy: "RIKEN (Japan)",
  },
  {
    number: 114, symbol: "Fl", name: "Flerovium", mass: [289],
    category: "post-transition", categoryLabel: "Post-transition metal",
    row: 7, col: 14, period: 7, group: 14, block: "p",
    config: "[Rn] 5f14 6d10 7s2 7p2", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 832, atomicRadius: null,
    commonCharges: null, commonOxidationStates: null,
    discovered: 1998, discoveredBy: "Joint Institute for Nuclear Research (Dubna)",
  },
  {
    number: 115, symbol: "Mc", name: "Moscovium", mass: [288],
    category: "post-transition", categoryLabel: "Post-transition metal",
    row: 7, col: 15, period: 7, group: 15, block: "p",
    config: "[Rn] 5f14 6d10 7s2 7p3", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 538, atomicRadius: null,
    commonCharges: null, commonOxidationStates: null,
    discovered: 2003, discoveredBy: "Joint Institute for Nuclear Research (Dubna)",
  },
  {
    number: 116, symbol: "Lv", name: "Livermorium", mass: [293],
    category: "post-transition", categoryLabel: "Post-transition metal",
    row: 7, col: 16, period: 7, group: 16, block: "p",
    config: "[Rn] 5f14 6d10 7s2 7p4", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 723, atomicRadius: null,
    commonCharges: null, commonOxidationStates: null,
    discovered: 2000, discoveredBy: "Joint Institute for Nuclear Research (Dubna)",
  },
  {
    number: 117, symbol: "Ts", name: "Tennessine", mass: [294],
    category: "halogen", categoryLabel: "Halogen",
    row: 7, col: 17, period: 7, group: 17, block: "p",
    config: "[Rn] 5f14 6d10 7s2 7p5", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 743, atomicRadius: null,
    commonCharges: null, commonOxidationStates: null,
    discovered: 2010, discoveredBy: "Joint Institute for Nuclear Research and Oak Ridge National Laboratory",
  },
  {
    number: 118, symbol: "Og", name: "Oganesson", mass: [294],
    category: "noble", categoryLabel: "Noble gas",
    row: 7, col: 18, period: 7, group: 18, block: "p",
    config: "[Rn] 5f14 6d10 7s2 7p6", state: "unknown",
    density: null, melt: null, boil: null,
    electroneg: null, electronAffinity: null, ionizationEnergy: 839, atomicRadius: null,
    commonCharges: null, commonOxidationStates: null,
    discovered: 2002, discoveredBy: "Joint Institute for Nuclear Research and Lawrence Livermore National Laboratory",
  },
];

// ─── Lookups ────────────────────────────────────────────────────────
// Internal indices for fast access. Built once at module load.

const BY_NUMBER = new Map();
const BY_SYMBOL = new Map();
for (const el of ELEMENTS) {
  BY_NUMBER.set(el.number, el);
  BY_SYMBOL.set(el.symbol, el);
}

// ─── Helper functions ──────────────────────────────────────────────

/**
 * Look up an element by symbol (case-sensitive) or atomic number.
 * Returns the element object, or null if not found.
 *
 * Examples:
 *   getElement("Cl")      → chlorine
 *   getElement(17)        → chlorine
 *   getElement("cl")      → null (case-sensitive)
 *   getElement("Pb")      → lead
 *   getElement(999)       → null
 */
export function getElement(symbolOrNumber) {
  if (typeof symbolOrNumber === "number") {
    return BY_NUMBER.get(symbolOrNumber) || null;
  }
  if (typeof symbolOrNumber === "string") {
    return BY_SYMBOL.get(symbolOrNumber) || null;
  }
  return null;
}

/**
 * All elements in a given group (1-18). Returns [] for invalid input
 * or for groups with no matching elements (e.g. group 99).
 *
 * Note: lanthanides (57-71) and actinides (89-103) have group: null
 * and are NOT returned by any getElementsByGroup call. This matches
 * the spec's f-block convention.
 *
 * Examples:
 *   getElementsByGroup(17) → halogens [F, Cl, Br, I, At, Ts]
 *   getElementsByGroup(1)  → alkali metals + H [H, Li, Na, K, Rb, Cs, Fr]
 *   getElementsByGroup(18) → noble gases [He, Ne, Ar, Kr, Xe, Rn, Og]
 */
export function getElementsByGroup(groupNumber) {
  return ELEMENTS.filter((el) => el.group === groupNumber);
}

/**
 * All elements in a given period (1-7).
 *
 * Period is the "true" period — lanthanides are period 6 (even though
 * they render in row 9 of the grid), actinides are period 7.
 *
 * Examples:
 *   getElementsByPeriod(2) → Li through Ne (8 elements)
 *   getElementsByPeriod(6) → Cs through Rn, including lanthanides (32 elements)
 */
export function getElementsByPeriod(periodNumber) {
  return ELEMENTS.filter((el) => el.period === periodNumber);
}

/**
 * All elements in a given block ("s", "p", "d", or "f").
 *
 * Examples:
 *   getElementsByBlock("s") → alkali + alkaline earth + H + He (14 elements)
 *   getElementsByBlock("p") → all p-block elements (36 elements)
 *   getElementsByBlock("d") → all transition + transactinide metals (40 elements)
 *   getElementsByBlock("f") → all lanthanides + actinides (30 elements: 57-71, 89-103, total 30)
 */
export function getElementsByBlock(block) {
  return ELEMENTS.filter((el) => el.block === block);
}

/**
 * All elements with a given category.
 *
 * Valid categories: alkali, alkaline, transition, post-transition,
 * metalloid, nonmetal, halogen, noble, lanthanide, actinide, unknown.
 *
 * Examples:
 *   getElementsByCategory("alkali")  → [Li, Na, K, Rb, Cs, Fr]
 *   getElementsByCategory("noble")   → [He, Ne, Ar, Kr, Xe, Rn, Og]
 *   getElementsByCategory("halogen") → [F, Cl, Br, I, At, Ts]
 */
export function getElementsByCategory(category) {
  return ELEMENTS.filter((el) => el.category === category);
}

// ─── getPeriodicNeighbors — for V64 distractor clustering ──────────
//
// Returns N other elements that share at least one of:
//   • same group (PREFERRED)
//   • same period (SECONDARY)
//   • same block (TERTIARY)
// with the input element.
//
// Selection priority: try same-group first; if fewer than N same-group
// neighbors, pad with same-period; if still short, pad with same-block;
// if still short, pad with random other elements.
//
// Returns an array of element SYMBOLS (not objects). This matches the
// V64 chem-quiz distractor pool API which works with symbol strings.
//
// V64 chem-Claude depends on this signature. Don't change it without
// coordinating with V64 chem quiz code.
//
// Examples:
//   getPeriodicNeighbors("Cl", 3)  → ["Br", "I", "F"]    (same-group halogens)
//   getPeriodicNeighbors("Cl", 5)  → ["Br", "I", "F", "At", "S"]
//                                    (3 halogens + Ts skipped (no measured EN, but
//                                     still in group 17 — actually IS included),
//                                     then period-3 neighbor S)
//   getPeriodicNeighbors("Fe", 3)  → 3 from same group 8 + nearby
//   getPeriodicNeighbors("Ds", 3)  → falls back to whole-table since
//                                    same-group 10 has only 4 entries and
//                                    most lack good distractor signal anyway
//
// If symbolOrNumber doesn't resolve to a valid element, returns [].
// If n <= 0, returns [].
// If n > 117 (max possible neighbors), capped at 117.
//
export function getPeriodicNeighbors(symbolOrNumber, n) {
  const el = getElement(symbolOrNumber);
  if (!el) return [];
  if (typeof n !== "number" || n <= 0) return [];
  n = Math.min(n, ELEMENTS.length - 1);

  // Tier 1: same group (excluding self). null-group elements (f-block)
  // skip this tier entirely — there is no group cluster.
  const sameGroup = el.group != null
    ? ELEMENTS.filter((other) => other.group === el.group && other.symbol !== el.symbol)
    : [];

  // Tier 2: same period (excluding self and already-collected same-group).
  const sameGroupSyms = new Set(sameGroup.map((e) => e.symbol));
  const samePeriod = ELEMENTS.filter((other) =>
    other.period === el.period &&
    other.symbol !== el.symbol &&
    !sameGroupSyms.has(other.symbol)
  );

  // Tier 3: same block (excluding self and already-collected).
  const collectedSyms = new Set([
    el.symbol,
    ...sameGroup.map((e) => e.symbol),
    ...samePeriod.map((e) => e.symbol),
  ]);
  const sameBlock = ELEMENTS.filter((other) =>
    other.block === el.block && !collectedSyms.has(other.symbol)
  );

  // Tier 4 fallback: anything else.
  for (const e of sameBlock) collectedSyms.add(e.symbol);
  const others = ELEMENTS.filter((other) => !collectedSyms.has(other.symbol));

  const ordered = [
    ...sameGroup,
    ...samePeriod,
    ...sameBlock,
    ...others,
  ];
  return ordered.slice(0, n).map((e) => e.symbol);
}
